import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Cloud,
  FileBox,
  Loader2,
  MoreVertical,
  PauseCircle,
  RefreshCw,
  StopCircle,
  Settings,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  PlayCircle,
  History,
  Plug,
  CircleAlert,
  ChevronDown,
  Clock,
  AtSign,
  FolderOpen,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime, formatTimeUTC } from '@/utils/dateUtils';
import {
  cloudSourceService,
  runOAuthPopup,
  CloudSourceListRow,
  CloudSyncProgress,
  CloudConnectionStatus,
  CloudCurrentStatus,
  CloudSyncFileStatus,
} from '@/services/cloudSourceService';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
import { useAuth } from '@/context/AuthContext';
import { useUserRoles } from '@/hooks/useUserRoles';
import CloudSourceConfigModal, {
  SUPPORTED_EXTENSIONS,
} from './CloudSourceConfigModal';
import CloudSourceActivityModal from './CloudSourceActivityModal';
import SharePointSitePickerDialog from './SharePointSitePickerDialog';

interface CloudSourceCardProps {
  source: CloudSourceListRow;
  onRefresh: () => void | Promise<void>;
  // Configured "Sync later" time of day (HH:MM, 24h). Used to label the
  // schedule button and the confirmation dialog with the actual time
  // instead of the misleading "midnight" baked into older copy.
  dailySyncTime?: string;
}

// Fast cadence - used only while the engine is actively working on this
// source (Discovering / Syncing / Queued, or the brief just-clicked window).
const POLL_INTERVAL_MS = 5000;
// Slow cadence for the "at rest but not finished" states: the between-batches
// gap (the source sits Idle/Complete while pending files wait for the */5-min
// `resume_stuck_batches` cron) and the downstream processing tail. Nothing this
// source can show changes faster than the cron in those states, so polling
// every 5s just spammed get_sync_progress with no new information. 30s keeps
// the card reasonably fresh while cutting at-rest calls ~6x.
const POLL_INTERVAL_IDLE_MS = 30000;
// Stop polling after this much time with no value changes. The resume cron
// runs every 5 min, so a 5-min window would give up *exactly* when the cron
// is about to advance a throttled sync - the counters sit still through the
// whole gap. Wait comfortably past one cron cycle so the card survives the
// between-batch lull; if nothing has moved after this long the engine is
// genuinely wedged (e.g. dead worker) and backing off is correct.
const POLL_STALE_TIMEOUT_MS = 15 * 60 * 1000;

function providerIcon(provider: string) {
  if (provider === 'SharePoint') return <FileBox className="h-5 w-5 text-blue-400" />;
  return <Cloud className="h-5 w-5 text-emerald-400" />;
}

/**
 * Friendly label for a SharePoint site webUrl. `.../sites/orgaindemo` →
 * "orgaindemo" (likewise for `/teams/`); falls back to the hostname, then the
 * raw string if it isn't a parseable URL. The full URL is shown on hover.
 */
function sharePointSiteLabel(url?: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    const i = parts.findIndex((p) => p === 'sites' || p === 'teams');
    if (i >= 0 && parts[i + 1]) return decodeURIComponent(parts[i + 1]);
    return u.hostname;
  } catch {
    return url;
  }
}

/** The site path portion of a SharePoint webUrl, e.g. "/sites/orgaindemo". */
function sharePointSitePath(url?: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).pathname || null;
  } catch {
    return null;
  }
}

function runtimeBadgeVariant(
  status: CloudCurrentStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'Syncing':
    case 'Discovering':
      return 'default';
    case 'Error':
      return 'destructive';
    case 'Complete':
      return 'secondary';
    default:
      return 'outline';
  }
}

/**
 * Card view for one connected cloud source. Owns its own poll loop for the
 * sync progress fields - kicks the loop on whenever the source enters an
 * active runtime status, and stops once it leaves.
 */
const CloudSourceCard: React.FC<CloudSourceCardProps> = ({ source, onRefresh, dailySyncTime }) => {
  // `dailySyncTime` is a UTC "HH:MM" from the backend; render it in the
  // viewer's local timezone. Falls back to UTC "00:00" so the UI degrades
  // gracefully when an older backend omits the field.
  const syncTimeLabel = formatTimeUTC(dailySyncTime || '00:00');
  // Connectors are org-wide: every admin sees this card and can operate it
  // (sync, retry, activity, folder/site scope) — those run server-side on
  // the STORED OAuth token, so who clicks is irrelevant to the result. Two
  // graded exceptions, mirrored on the backend (_load_source_with_perm):
  // - Disconnect / Delete (destroy): owner or System Manager — breaking
  //   the org's ingestion pipe stays restricted.
  // - Reconnect (owner ONLY): the consent popup authenticates WHOEVER
  //   clicks, so a non-owner would silently rebind the connector to their
  //   own cloud identity and change what the org ingests.
  const { user } = useAuth();
  const { roles, isSuperAdmin } = useUserRoles();
  const currentUserId = (user?.email || '').toLowerCase();
  const isOwner =
    !!currentUserId && source.owner.toLowerCase() === currentUserId;
  const canDestroy =
    isOwner ||
    isSuperAdmin ||
    (roles || []).some((r) => r.name === 'System Manager');
  const [progress, setProgress] = useState<CloudSyncProgress | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  // Which status bucket the activity modal should open on. Clicking a "Skipped"
  // count deep-links into the Skipped filter; the kebab "View activity" opens
  // 'All'. Remembered here so it's set before the modal mounts.
  const [activityFilter, setActivityFilter] = useState<'All' | CloudSyncFileStatus>('All');
  const openActivity = (filter: 'All' | CloudSyncFileStatus = 'All') => {
    setActivityFilter(filter);
    setShowActivity(true);
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  // Sync-time guard for account mismatches: when the OAuth identity
  // (`connected_account_email`) differs from the Frappe login (`owner`),
  // the user might be about to ingest files from the wrong identity (e.g.
  // a personal Drive vs the work one they intended to connect). We require
  // an explicit confirm before triggering the sync rather than blocking
  // outright - admins occasionally connect on behalf of someone else.
  const [confirmMismatchSync, setConfirmMismatchSync] = useState(false);
  // Sync mode (now / later) requested when the confirmation dialog opens -
  // we remember it so handleConfirmMismatchSync can pass it back to runSync.
  // Defaults to 'now' for the server-side mismatch fallback path that doesn't
  // go through handleSyncNow.
  const [pendingSyncMode, setPendingSyncMode] = useState<'now' | 'later'>('now');
  // Sync-now-over-scheduled guard: when the user picks "Sync now" but a
  // midnight run is already queued (`next_scheduled_sync_at` is set), we
  // pause to confirm - running now cancels the scheduled run so we don't
  // sync the same source twice in a few hours.
  const [confirmOverrideScheduled, setConfirmOverrideScheduled] = useState(false);
  // SharePoint-only: allow swapping the site after initial connect. The
  // dialog component handles list-fetch, search, and persistence; we just
  // toggle it open and refresh on save.
  const [showChangeSite, setShowChangeSite] = useState(false);
  // True while the Configure modal is being shown as the mandatory folder-setup
  // step (no Cancel / dismiss). Driven by the `needsFolders` effect below.
  const [configMandatory, setConfigMandatory] = useState(false);
  // After the site picker saves, just refresh - the `needsFolders` effect picks
  // up the new state (site set, scope cleared) and opens the mandatory folder
  // dialog. No need to open it imperatively here.
  const handleSiteSaved = async () => {
    await onRefresh();
  };

  const [busy, setBusy] = useState<
    null | 'sync' | 'retry' | 'disconnect' | 'reconnect' | 'delete' | 'pause' | 'stop'
  >(null);

  const pollTimerRef = useRef<number | null>(null);
  // Track the last poll response's "fingerprint" so we can detect when the
  // engine has stopped producing change. Stale-timeout is reset whenever
  // any tracked field moves.
  const lastFingerprintRef = useRef<string>('');
  const lastChangeAtRef = useRef<number>(0);
  // When polling pauses itself due to staleness, this flips true. A user
  // action (Sync now / Retry / Reconnect) or an explicit "Refresh" click
  // resets it.
  const [pollStalled, setPollStalled] = useState(false);

  // Epoch-ms until which we force the poll loop on, regardless of the
  // current status. Right after the user clicks "Sync now" the backend
  // job is only QUEUED - `current_status` is still Idle/Complete and the
  // counters are 0, so `shouldPoll` (status/pending based) would be
  // false and the progress bar wouldn't appear until a manual page
  // refresh. We KNOW a job was queued, so we bridge the gap by polling
  // for a fixed window; once `discover_source` flips the status to
  // Discovering/Syncing, the normal `isActive` path takes over and the
  // window becomes irrelevant. Self-limiting: if the job never starts
  // (worker down) polling stops when the window elapses.
  const [forcePollUntil, setForcePollUntil] = useState<number>(0);
  const [retryQueuedUntil, setRetryQueuedUntil] = useState<number>(0);
  const [retryInFlight, setRetryInFlight] = useState<boolean>(false);

  // Drop the local poll snapshot when the parent re-fetches the source
  // (source.modified changes). The poll loop captures a `progress` blob
  // that the render path prefers over the source prop, so without this
  // a stale snapshot from before a Reconnect / Disconnect / Sync trigger
  // would mask the fresh server state (e.g. connection_status stuck on
  // "Disconnected" + the old error message + missing Sync button) until
  // the user manually refreshed the page. Discarding the snapshot lets
  // the next render fall through to `source.*`, and any active poll
  // will repopulate `progress` on its next tick.
  const lastSourceModifiedRef = useRef(source.modified);
  useEffect(() => {
    if (source.modified !== lastSourceModifiedRef.current) {
      lastSourceModifiedRef.current = source.modified;
      setProgress(null);
    }
  }, [source.modified]);

  const currentStatus: CloudCurrentStatus =
    (progress?.current_status as CloudCurrentStatus) || source.current_status;
  const connectionStatus: CloudConnectionStatus =
    (progress?.connection_status as CloudConnectionStatus) || source.connection_status;
  const pendingCount = progress?.pending_count ?? source.pending_count ?? 0;
  // Files downloaded and handed to the document pipeline (chunk/embed/entity)
  // but not yet confirmed Synced/Failed. The cloud-source `current_status`
  // returns to Complete/Idle once downloads finish, so this is the only signal
  // that documents are still being processed downstream.
  const processingCount = progress?.processing_count ?? source.processing_count ?? 0;

  // Canonical runtime state, computed server-side (compute_display_state) and
  // returned by both the list endpoint and the poll endpoint. The card renders
  // this instead of re-deriving state from status+counters+enabled - deriving
  // client-side is exactly how the frozen-paused-card bug happened. The local
  // fallback below mirrors the backend rules for a backend that predates the
  // field (rolling deploy only).
  const displayState: string =
    progress?.display_state ??
    source.display_state ??
    (() => {
      if (
        currentStatus === 'Discovering' ||
        currentStatus === 'Syncing' ||
        currentStatus === 'Queued'
      )
        return currentStatus;
      if (processingCount > 0) return 'Processing';
      if (!source.enabled) return 'Paused';
      if (currentStatus === 'Error') return 'Error';
      if (pendingCount > 0) return 'Queued';
      return 'Idle';
    })();

  // Paused = operator flipped `enabled` off. Queued files are parked (the
  // resume cron skips disabled sources), so pending work must not read as
  // "in progress" and the controls stay usable. A live processing tail wins
  // over Paused server-side, so paused-with-tail still shows Processing.
  const isPaused = displayState === 'Paused';

  // The connection is the gate for everything else. When it's healthy we show
  // the normal controls (Sync / Configure / runtime status); when it's broken
  // we collapse the card to a single error message + Reconnect so the user
  // isn't offered Sync buttons that can only fail.
  const connectionHealthy = connectionStatus === 'Connected';
  // A genuine connection ERROR the user must resolve (vs. neutral states like
  // Disconnected / mid-connect). Drives the red banner + prominent Reconnect.
  const connectionErrored =
    connectionStatus === 'Expired' || connectionStatus === 'Error';
  // One human message for the problem - prefer the server's friendly text,
  // fall back to a sensible default so we never show a bare badge.
  const connectionMessage =
    source.connection_error ||
    source.last_error ||
    `Your ${source.provider} connection needs attention. Use Reconnect to resume syncing.`;

  // `isActive` drives spinner + Sync-now-button visibility: the engine is
  // genuinely working on this source (Stopping = still working, draining an
  // abort request).
  const isActive =
    displayState === 'Discovering' ||
    displayState === 'Syncing' ||
    displayState === 'Stopping';

  // Engine-queued specifically: the discover_source JOB is enqueued but no
  // worker has claimed it (vs. displayState 'Queued', which also covers the
  // between-batches backlog). Drives the "waiting for another sync" copy and
  // the fast poll lane only.
  const isQueued = currentStatus === 'Queued';

  // `inProgress` is the user-facing "this sync is still running": actively
  // working, backlog queued for the resume cron, or the document-pipeline
  // tail. All three are display states now - Paused/Idle/Error mean at rest.
  const inProgress =
    isActive || displayState === 'Queued' || displayState === 'Processing';

  // Pause clicked but the in-flight batch is still draining: the source is
  // already disabled while display_state still shows the honest tail
  // (Syncing/Processing). Without this the Pause button stayed clickable
  // through the drain, which read as "pause didn't take" and invited
  // rage-clicks. Drives the frozen "Pausing…" button + info line.
  const isPausing = !source.enabled && !isPaused && inProgress;

  // Poll while anything is moving; `forcePollUntil` bridges the "job queued
  // but not started" gap right after a Sync-now click - see the state
  // declaration above.
  const shouldPoll = inProgress || Date.now() < forcePollUntil;

  // Fast lane while the engine works (live progress wanted); slow lane for
  // the between-batches / processing drain. Listed in the poll effect's deps
  // so the cadence switches on transitions.
  const pollFast = isActive || isQueued || Date.now() < forcePollUntil;

  // Lock all mutating controls (Sync now/later, Configure gear, overflow kebab)
  // while a run is underway - active sync, the queued-between-batches gap, OR
  // the downstream document-processing tail - so the user can't change settings
  // or kick off another sync mid-flight. `busy` covers an in-flight user action.
  const controlsLocked = busy !== null || inProgress;

  // OAuth identity vs Frappe identity. Plan calls for a warning when the
  // user signed in to Google/Microsoft with a different email than their
  // Frappe login - they might be connecting the wrong account. Compute
  // client-side from the two emails already on the row; no backend flag
  // needed.
  const accountMismatch =
    connectionStatus === 'Connected' &&
    !!source.connected_account_email &&
    !!source.owner &&
    source.connected_account_email.toLowerCase() !== source.owner.toLowerCase();

  // Provider-accurate noun for the file container, used in the confirm-sync
  // dialog copy. Google Drive syncs a personal "drive"; SharePoint syncs a
  // shared "site" document library - so "the connected account's drive, not
  // yours" is misleading for SharePoint, where the site is shared and only
  // the access token differs. Branch the wording on this.
  const isDriveProvider = source.provider === 'Google Drive';
  const providerStoreNoun = isDriveProvider ? 'drive' : 'site';

  const fetchProgress = useCallback(async () => {
    try {
      const p = await cloudSourceService.getSyncProgress(source.name);
      setProgress(p);
      // Track value movement. If counters + status haven't changed for
      // POLL_STALE_TIMEOUT_MS, flip the stalled flag - the poll effect
      // tears down the interval until a user action re-arms it.
      const fp = [
        p.current_status,
        p.display_state,
        p.total_discovered,
        p.pending_count,
        p.synced_count,
        p.processing_count,
        p.skipped_count,
        p.failed_count,
        p.stale_count,
      ].join('|');
      const now = Date.now();
      if (fp !== lastFingerprintRef.current) {
        lastFingerprintRef.current = fp;
        lastChangeAtRef.current = now;
      } else if (now - lastChangeAtRef.current >= POLL_STALE_TIMEOUT_MS &&
        (p.processing_count ?? 0) === 0
      ) {
        setPollStalled(true);
      }
    } catch (err) {
      logger.warn('Sync progress poll failed', err);
    }
  }, [source.name]);

  useEffect(() => {
    if (!shouldPoll || pollStalled) {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    // (Re)start: reset staleness tracking so the timeout is counted from
    // *this* moment, not from whenever the card first mounted.
    lastFingerprintRef.current = '';
    lastChangeAtRef.current = Date.now();
    fetchProgress();
    // Fast while the engine is actively working; slow while only draining
    // pending/processing at rest - the latter was the source of frequent
    // get_sync_progress calls when nothing was actually syncing.
    const intervalMs = pollFast ? POLL_INTERVAL_MS : POLL_INTERVAL_IDLE_MS;
    pollTimerRef.current = window.setInterval(fetchProgress, intervalMs);
    return () => {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [shouldPoll, pollFast, pollStalled, fetchProgress]);

  // When polling stops (pending hit 0 or status reached a terminal idle
  // state), ask the parent to refresh - list_my_sources surfaces the final
  // aggregate counts and any other fields the poll endpoint doesn't return.
  const prevShouldPollRef = useRef(shouldPoll);
  useEffect(() => {
    if (prevShouldPollRef.current && !shouldPoll) {
      // Drop the cached poll snapshot. The render path prefers `progress` over
      // the `source` prop, and the poll loop NEVER cleared its last value when
      // it stopped - so after a sync ended the card kept painting the final
      // poll septet indefinitely. If a later re-discovery shrank the file set
      // (e.g. 48 → 39) the stale snapshot could show impossible combinations
      // like "Synced 48 / Discovered 39". Clearing here makes `totals` fall
      // back to the freshly-refetched `source` (a single consistent snapshot).
      // Aggregate writes use update_modified=False, so the source.modified
      // effect can't be relied on to discard it - we must do it explicitly.
      setProgress(null);
      onRefresh();
      // The retry-triggered batch (if any) has finished - release the
      // failed-count hide so the final tally renders.
      if (retryInFlight) {
        setRetryInFlight(false);
      }
    }
    prevShouldPollRef.current = shouldPoll;
  }, [shouldPoll, onRefresh, retryInFlight]);

  const runSync = async (
    allowMismatch: boolean = false,
    when: 'now' | 'later' = 'now',
  ) => {
    setBusy('sync');
    setPollStalled(false);
    try {
      // Always force_full on user-triggered sync. Delta queries are an
      // optimisation for the cron path; a human clicking Sync now expects
      // "pick up everything", not "what changed since last time".
      const res = await cloudSourceService.triggerSync(
        source.name, true, allowMismatch, when,
      );
      if (res.status === 'error') {
        // Server can also block on account mismatch (in case the client
        // skipped the confirmation, e.g. stale UI state). Surface the same
        // dialog rather than a bare error toast.
        if (res.code === 'account_mismatch') {
          setConfirmMismatchSync(true);
          return;
        }
        // "Source already in 'Syncing'" means our cached current_status is
        // stale - there's a sync running we didn't know about. Treat as info
        // and refresh so the card flips to the live view.
        const alreadyRunning = /already in/i.test(res.message || '');
        if (alreadyRunning) {
          toast.info('A sync is already in progress');
        } else {
          toast.error(res.message || 'Could not start sync');
        }
        await fetchProgress();
        return;
      }
      if (res.status === 'scheduled') {
        const when_label = res.scheduled_at
          ? formatDateTime(res.scheduled_at)
          : 'the next scheduled time';
        toast.success(`Sync scheduled for ${when_label}`);
      } else if (res.queued_behind_active) {
        // Another sync is already running, so this job will wait in the
        // queue. Match the card's "Queued - waiting…" banner instead of
        // claiming it started.
        toast.success('Sync queued - waiting for the current sync to finish');
      } else {
        if (res.scheduled_sync_cancelled) {
          toast.success('Sync started - replaces the scheduled run');
        } else {
          toast.success('Sync started');
        }
        // The job is QUEUED, not running yet - current_status is still
        // Idle/Complete so status-based polling wouldn't kick in. Force
        // the poll loop on for a window so the progress bar appears
        // immediately; once discover_source flips the status the normal
        // isActive path keeps it alive. 90s comfortably covers RQ pickup
        // latency even when the long queue is busy.
        setForcePollUntil(Date.now() + 90_000);
      }
      // Optimistically begin polling - backend will flip status shortly.
      // Also ask the parent to refresh so the scheduled badge clears (the
      // backend sets next_scheduled_sync_at=NULL when a manual "now" sync
      // pre-empts a queued midnight one).
      await fetchProgress();
      onRefresh();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Sync failed');
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const handleSyncNow = () => {
    // Always confirm before triggering a manual sync - the user wants to
    // see the scope (folder paths + file extension filter) and, when the
    // OAuth account differs from their Frappe login, the account warning.
    // The override-scheduled prompt is downstream of confirmation.
    setPendingSyncMode('now');
    setConfirmMismatchSync(true);
  };

  const handleSyncLater = () => {
    // Same confirmation flow as "Sync now" - even though the work doesn't
    // start immediately, we still want the user to acknowledge what'll be
    // pulled (and from which account) when the scheduled run fires.
    setPendingSyncMode('later');
    setConfirmMismatchSync(true);
  };

  const handleConfirmMismatchSync = async () => {
    setConfirmMismatchSync(false);
    // Only escalate the override-scheduled prompt for "now" - "later"
    // doesn't pre-empt the scheduled run.
    if (
      pendingSyncMode === 'now' &&
      !accountMismatch &&
      source.next_scheduled_sync_at
    ) {
      setConfirmOverrideScheduled(true);
      return;
    }
    await runSync(accountMismatch, pendingSyncMode);
  };

  const handleConfirmOverrideScheduled = async () => {
    setConfirmOverrideScheduled(false);
    // Account-mismatch was already cleared above; pass false here.
    await runSync(false, 'now');
  };

  const handleRetryFailed = async () => {
    setBusy('retry');
    setPollStalled(false);
    try {
      const res = await cloudSourceService.retryFailed(
        source.name, accountMismatch,
      );
      if (res.status === 'error') {
        if (res.code === 'account_mismatch') {
          setConfirmMismatchSync(true);
        } else {
          toast.error(res.message || 'Retry failed');
        }
        return;
      }
      // reset_count === 0 is a valid "nothing to do" outcome (no failed
      // files, or only poison files) - surface the server's message as
      // info rather than a misleading "Reset 0 files" success.
      if ((res.reset_count ?? 0) === 0) {
        toast.info(res.message || 'No failed files to retry.');
      } else {
        toast.success(
          res.message || `Reset ${res.reset_count} failed file(s) for retry.`,
        );
        // A retry sync was queued - bridge the poll gap like Sync now,
        // and disable the button itself so it can't be re-clicked before
        // the engine flips into Discovering/Syncing.
        setForcePollUntil(Date.now() + 90_000);
        setRetryQueuedUntil(Date.now() + 90_000);
        setRetryInFlight(true);
      }
      fetchProgress();
      onRefresh();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Retry failed');
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const handleReconnect = async () => {
    setBusy('reconnect');
    try {
      const r = await cloudSourceService.reconnect(source.name);
      if (r.redirect_url) {
        await runOAuthPopup(source.name, r.redirect_url);
        toast.success('Reconnected');
      } else {
        // The connection still probed healthy, so the backend reused it
        // rather than tearing it down. No popup, no account chooser.
        toast.success(r.message || 'Already connected');
      }
      // Drop any pre-reconnect poll snapshot eagerly so the Connected
      // badge, cleared error message, and Sync button surface as soon as
      // the parent refresh lands - without this the next render uses the
      // stale "Disconnected/Error" values until the source.modified
      // effect fires.
      setProgress(null);
      setPollStalled(false);
      await onRefresh();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Reconnect failed');
      toast.error(msg);
      // The popup rejects on a declined scope (backend just demoted the
      // source to "Error" with an actionable message) as well as on a
      // genuine cancel. Refresh so the card reflects the fresh server
      // state - Error badge, the reconnect banner, the Reconnect button -
      // instead of the stale pre-reconnect view.
      setProgress(null);
      setPollStalled(false);
      await onRefresh();
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    setBusy('disconnect');
    try {
      await cloudSourceService.disconnect(source.name);
      toast.success('Disconnected');
      setConfirmDisconnect(false);
      setProgress(null);
      setPollStalled(false);
      await onRefresh();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Disconnect failed');
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    setBusy('delete');
    try {
      const res = await cloudSourceService.deleteSource(source.name, true);
      // Backend pre-condition failure (e.g. the source still has documents in
      // the KB, or a sync is running) comes back through the success channel
      // with a ready-to-show message. Keep the dialog open so the user can act
      // on it (Disconnect instead, remove the documents, etc.).
      if (res.status === 'error') {
        toast.error(res.message || 'Could not delete this source.');
        return;
      }
      setConfirmDelete(false);
      await onRefresh();
      // Emit a single toast. When the backend returns cleanup guidance
      // (e.g. revoke the OAuth grant), fold it into the same success toast
      // instead of firing a second one - two sonner toasts for one action
      // stack with an offset and overlap awkwardly. Sonner's default
      // description color is very low-contrast; override it so the
      // instruction stays readable.
      const tip = res.cleanup_guidance?.[0];
      if (tip) {
        toast.success('Connection removed', {
          description: (
            <span className="text-foreground/80">
              <strong className="font-medium">{tip.label}.</strong> {tip.detail}
            </span>
          ),
          action: tip.url
            ? {
                label: 'Open',
                onClick: () => window.open(tip.url, '_blank', 'noopener,noreferrer'),
              }
            : undefined,
          duration: 10000,
        });
      } else {
        toast.success('Connection removed');
      }
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Delete failed');
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  // Pause / resume = flip the source's `enabled` flag via save_source (the
  // one upsert endpoint for create / update / enable / disable). Pause is the
  // safety valve for runaway syncs: the */5 cron stops claiming batches, the
  // queue is preserved, and OAuth stays intact - resuming later needs no
  // reconnect. Deliberately gated on `busy` only, NOT `controlsLocked`:
  // pausing mid-sync is the exact capability the lock would otherwise deny.
  const handleTogglePaused = async () => {
    setBusy('pause');
    try {
      await cloudSourceService.saveSource({
        source: source.name,
        enabled: isPaused ? 1 : 0,
      });
      toast.success(
        isPaused
          ? 'Sync resumed - queued files continue within 5 minutes'
          : 'Sync paused - queued files will wait until you resume',
      );
      setProgress(null);
      setPollStalled(false);
      if (isPaused) {
        // Resuming: the cron pick-up is invisible to status-based polling
        // until it flips the source active - bridge the gap like Sync now.
        setForcePollUntil(Date.now() + 90_000);
      }
      await onRefresh();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(
        err,
        isPaused ? 'Resume failed' : 'Pause failed',
      );
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  // Abort the running sync (non-destructive - see backend stop_sync). Gated
  // on `busy` only, same reasoning as Pause: aborting a runaway sync is the
  // exact control that must stay reachable while everything else is locked.
  const handleStopSync = async () => {
    setBusy('stop');
    try {
      const r = await cloudSourceService.stopSync(source.name);
      toast.success(r.message || 'Stop requested');
      setConfirmStop(false);
      setProgress(null);
      setPollStalled(false);
      // Keep polling through the drain so the card shows Stopping -> Idle.
      setForcePollUntil(Date.now() + 90_000);
      await onRefresh();
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Stop failed');
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const totals = progress ?? {
    total_discovered: source.total_discovered,
    pending_count: source.pending_count,
    synced_count: source.synced_count,
    processing_count: source.processing_count,
    skipped_count: source.skipped_count,
    failed_count: source.failed_count,
    stale_count: source.stale_count,
  };

  // While a retry is queued but the backend hasn't yet flipped failed→Pending
  // in the row store (poll lag), optimistically render the failed files as
  // already Pending. The math invariant `failed + pending = constant` across
  // the bucket flip means these displayed values stay consistent before AND
  // after the backend update propagates back via poll.
  const displayedTotals = retryInFlight
    ? {
        ...totals,
        pending_count: (totals.pending_count ?? 0) + (totals.failed_count ?? 0),
        failed_count: 0,
      }
    : totals;

  // Invariant guard (defense-in-depth alongside the snapshot clear above):
  // server-side `synced_count` is always a subset of `total_discovered`, so
  // Synced can never legitimately exceed Discovered. Should any torn/stale
  // read still surface that, clamp the displayed Synced to Discovered rather
  // than render a structurally impossible (and falsely reassuring) number.
  // A no-op in every consistent state.
  const safeSyncedCount = Math.min(
    displayedTotals.synced_count ?? 0,
    displayedTotals.total_discovered ?? 0,
  );

  // "Checked, nothing changed" signal. `last_discovery_at` advances on every
  // successful discovery pass; `last_activity_at` only moves when files are
  // actually processed. So when discovery is newer than activity (or activity
  // never happened), the most recent run re-checked the provider and found
  // nothing new to ingest - a no-op resync. Surfacing this gives the user
  // visible confirmation the sync ran, instead of a "Last activity" timestamp
  // that looks frozen after they clicked Sync now.
  const checkedNoChanges =
    !!source.last_discovery_at &&
    (!source.last_activity_at ||
      new Date(source.last_discovery_at).getTime() >
        new Date(source.last_activity_at).getTime());

  // A SharePoint source can be Connected (Microsoft token stored) yet have no
  // site chosen - the user abandoned the mandatory site picker (e.g. closed
  // the tab after sign-in). It can't sync without a site, so surface a "finish
  // setup" prompt and hold the Sync buttons back until a site is picked.
  const needsSite =
    source.provider === 'SharePoint' &&
    connectionStatus === 'Connected' &&
    !source.sharepoint_site_url;

  // "Needs folder config": connected but the user hasn't scoped folders yet
  // AND hasn't confirmed a whole-drive/site scan. A persistent, backend-derived
  // state (mirrors `needsSite`) - it survives a page reload and holds back the
  // Sync buttons until the user picks folders (or confirms a whole-drive scan)
  // via the Configure dialog. The matching backend guard in trigger_sync
  // rejects a sync in this state.
  //   - SharePoint: fires once a site is chosen but no folders are scoped.
  //   - Google Drive: fires on every fresh connect (first-time AND
  //     different-account reconnect). The OAuth-completion path drops the
  //     whole-drive confirmation so the user must make a deliberate folder /
  //     whole-drive choice. Already-configured sources keep their folder IDs
  //     or confirm_whole_drive_scan=1, so this does NOT trip for them.
  const needsFolders =
    connectionStatus === 'Connected' &&
    !source.confirm_whole_drive_scan &&
    ((source.provider === 'SharePoint' &&
      !!source.sharepoint_site_url &&
      !(source.sharepoint_folder_paths || '').trim()) ||
      (source.provider === 'Google Drive' &&
        !(source.drive_folder_ids || '').trim()));

  // Human summary of the configured sync scope, shown as "Target Directory".
  const targetDirectoryLabel = (() => {
    if (source.provider === 'SharePoint') {
      if (!source.sharepoint_site_url) return 'No site selected yet';
      const base =
        sharePointSitePath(source.sharepoint_site_url) ||
        source.sharepoint_site_name ||
        sharePointSiteLabel(source.sharepoint_site_url) ||
        '';
      const folders = (source.sharepoint_folder_paths || '')
        .split(/[\n,]/)
        .map((f) => f.trim())
        .filter(Boolean);
      if (source.confirm_whole_drive_scan) return base + ' (entire site)';
      if (folders.length === 0) return base;
      if (folders.length === 1) return base + '/' + folders[0].replace(/^\//, '');
      return base + ' - ' + folders.length + ' folders';
    }
    if (source.confirm_whole_drive_scan) return 'Entire Google Drive';
    // Despite the field name, `drive_folder_ids` holds human folder PATHS, not
    // opaque Drive IDs - the folder picker stores what it displays and
    // _normalize_folder_list() rejects anything URL-like. So show the path, the
    // way SharePoint does, instead of an unhelpful "N folders selected".
    const folders = (source.drive_folder_ids || '')
      .split(/[\n,]/)
      .map((f) => f.trim())
      .filter(Boolean);
    if (folders.length === 0) return 'Not configured yet';
    if (folders.length === 1) return folders[0];
    return folders.length + ' folders';
  })();

  const showSyncButton =
    !isActive &&
    connectionStatus === 'Connected' &&
    !!source.enabled &&
    !needsSite &&
    !needsFolders;

  useEffect(() => {
    if (needsFolders) {
      setConfigMandatory(true);
      setShowConfig(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFolders]);

  return (
    <Card className="overflow-hidden border-border bg-card">
      <CardContent className="p-0">
        {/* Header: provider identity + connection dot | runtime badge */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40">
              {providerIcon(source.provider)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-semibold">{source.provider}</p>
              <p
                className={
                  'mt-0.5 flex items-center gap-1.5 text-xs font-medium ' +
                  (connectionHealthy
                    ? 'text-emerald-400'
                    : connectionErrored
                      ? 'text-red-400'
                      : 'text-muted-foreground')
                }
              >
                <span
                  className={
                    'inline-block h-1.5 w-1.5 rounded-full ' +
                    (connectionHealthy
                      ? 'bg-emerald-400'
                      : connectionErrored
                        ? 'bg-red-400'
                        : 'bg-muted-foreground/60')
                  }
                />
                {connectionStatus}
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {/* Runtime badge = the backend's display_state, rendered verbatim.
                Only when the connection is healthy (the dot on the left tells
                the connection story) and only when there's something to say -
                Idle renders no badge, same as before. */}
            {connectionHealthy && displayState !== 'Idle' && (() => {
              const spinning =
                isActive || displayState === 'Processing';
              const variant =
                displayState === 'Processing'
                  ? 'default'
                  : displayState === 'Queued' || displayState === 'Paused'
                    ? 'outline'
                    : displayState === 'Error'
                      ? runtimeBadgeVariant('Error')
                      : runtimeBadgeVariant(currentStatus);
              return (
                <Badge variant={variant}>
                  {spinning ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : displayState === 'Queued' ? (
                    <Clock className="h-3 w-3 mr-1" />
                  ) : displayState === 'Paused' ? (
                    <PauseCircle className="h-3 w-3 mr-1" />
                  ) : null}
                  {displayState}
                </Badge>
              );
            })()}
          </div>
        </div>

        <div className="space-y-3 px-4 pb-4 pt-3">
          {/* Authenticated account */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Authenticated Account
            </p>
            <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
              <AtSign className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="truncate text-sm">
                {source.connected_account_email || (
                  <span className="italic text-muted-foreground/70">
                    {connectionStatus === 'Connected'
                      ? 'Account email unavailable'
                      : 'No account connected'}
                  </span>
                )}
              </span>
            </div>
            {!isOwner && (
              // Another admin made this connection. Surface who, so a second
              // admin doesn't try to duplicate the work — and knows who to
              // ask when a reconnect (owner-only) is needed.
              <p className="mt-1.5 text-xs text-muted-foreground" title={source.owner}>
                Connected by{' '}
                <span className="font-medium text-foreground">
                  {source.owner_full_name || source.owner}
                </span>
              </p>
            )}
          </div>

          {/* Toolbar: last sync + every source action (sync split button,
              retry, reconnect, configure gear, overflow kebab) */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
            <div className="mr-auto min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Last Sync
              </p>
              <p className="text-xs font-medium">
                {source.last_activity_at || source.last_discovery_at
                  ? formatDateTime(source.last_activity_at || source.last_discovery_at)
                  : 'Never'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {needsSite && (
                <Button
                  size="sm"
                  onClick={() => setShowChangeSite(true)}
                  disabled={busy !== null}
                >
                  <FileBox className="mr-1.5 h-3.5 w-3.5" />
                  Pick a site
                </Button>
              )}
              {showSyncButton && (
                <div className="flex items-stretch">
                  <Button
                    size="sm"
                    onClick={handleSyncLater}
                    disabled={controlsLocked}
                    className="rounded-r-none"
                  >
                    {busy === 'sync' ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Clock className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Sync later
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        disabled={controlsLocked}
                        className="rounded-l-none border-l border-l-background/30 px-2"
                        aria-label="More sync options"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="dark bg-background text-foreground">
                      <DropdownMenuItem onClick={handleSyncLater} disabled={controlsLocked}>
                        <Clock className="mr-2 h-3.5 w-3.5" />
                        Sync later
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleSyncNow} disabled={controlsLocked}>
                        <PlayCircle className="mr-2 h-3.5 w-3.5" />
                        Sync now (manual)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              {/* Resume: paused source with a healthy connection. Takes the
                  place of the Sync buttons (showSyncButton requires enabled).
                  Gated on `busy` only - never controlsLocked. */}
              {connectionHealthy && isPaused && !needsSite && !needsFolders && (
                <Button
                  size="sm"
                  onClick={handleTogglePaused}
                  disabled={busy !== null}
                >
                  {busy === 'pause' ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <PlayCircle className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Resume sync
                </Button>
              )}
              {/* Stop: abort the run. In-flight files finish, the unstarted
                  remainder returns to the queue, and nothing auto-restarts
                  until the user syncs again. Non-destructive - queue cleanup
                  belongs to folder changes, never to Stop. */}
              {/* Hidden while Pausing: the drain is already finishing and a
                  Stop on top would only tangle the two transitions - one
                  in-flight intent at a time. */}
              {connectionHealthy && inProgress && displayState !== 'Stopping' && !isPausing && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setConfirmStop(true)}
                  disabled={busy !== null}
                >
                  {busy === 'stop' ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <StopCircle className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Stop
                </Button>
              )}
              {connectionHealthy && displayState === 'Stopping' && (
                <Button size="sm" variant="outline" disabled>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Stopping…
                </Button>
              )}
              {/* Pause: the escape hatch while a run is underway. MUST stay
                  clickable when everything else is controlsLocked - a runaway
                  sync is exactly when the operator needs it - so it's gated on
                  `busy` only. */}
              {connectionHealthy && isPausing && (
                // Pause already accepted - the in-flight batch is draining.
                // Frozen on purpose: a live Pause button here reads as "the
                // click didn't work" and invites repeat clicks.
                <Button size="sm" variant="outline" disabled>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Pausing sync…
                </Button>
              )}
              {/* Hidden while Stopping - mirror of the Stop-during-Pausing
                  case above. */}
              {connectionHealthy && !isPaused && !isPausing && inProgress &&
                displayState !== 'Stopping' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTogglePaused}
                  disabled={busy !== null}
                >
                  {busy === 'pause' ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <PauseCircle className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Pause sync
                </Button>
              )}
              {connectionHealthy && !isActive && !retryInFlight && (totals.failed_count ?? 0) > 0 && (() => {
                const retryQueued = Date.now() < retryQueuedUntil;
                const isRetrying = busy === 'retry' || retryQueued;
                return (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetryFailed}
                    // Lock Retry while a run is underway (queued, between-batches,
                    // or downstream document processing) just like Sync / gear /
                    // kebab - `controlsLocked` folds in both `busy` and the
                    // broader `inProgress`. Without this, Retry stayed clickable
                    // during the queued/processing gap (where `isActive` is
                    // false) while every other control was disabled.
                    disabled={controlsLocked || retryQueued}
                    title={
                      inProgress && busy === null
                        ? 'Unavailable while a sync is in progress'
                        : undefined
                    }
                  >
                    {isRetrying ? (
                      <>
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        Retrying {totals.failed_count}…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                        Retry failed ({totals.failed_count})
                      </>
                    )}
                  </Button>
                );
              })()}
              {connectionStatus !== 'Connected' && !isActive && isOwner && (
                <Button
                  size="sm"
                  variant={connectionErrored ? 'default' : 'outline'}
                  onClick={handleReconnect}
                  disabled={busy !== null}
                >
                  {busy === 'reconnect' ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plug className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Reconnect
                </Button>
              )}

              {/* Configure gear: only meaningful for a working connection -
                  hidden during an active sync (applies to the next run only),
                  when the connection is broken (Reconnect comes first), AND
                  before a SharePoint site is picked (folders / scope can't be
                  configured until the source has a site - Pick a site first). */}
              {!isActive && connectionHealthy && !needsSite && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setShowConfig(true)}
                  disabled={controlsLocked}
                  title={controlsLocked ? 'Unavailable while processing' : 'Configure'}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}

              {/* Overflow kebab stays available even when disconnected - it's
                  the only home for Disconnect / Delete / View activity, which
                  the user still needs on a broken source. Hidden only during
                  an active sync (those actions conflict with the running job). */}
              {!isActive && (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        disabled={controlsLocked}
                        title={controlsLocked ? 'Unavailable while processing' : undefined}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="dark bg-background text-foreground">
                      <DropdownMenuItem onClick={() => openActivity('All')}>
                        <History className="mr-2 h-4 w-4" />
                        View activity
                      </DropdownMenuItem>
                      {source.provider === 'SharePoint' &&
                        connectionStatus === 'Connected' && (
                          <DropdownMenuItem
                            onClick={() => setShowChangeSite(true)}
                            disabled={busy !== null}
                          >
                            <FileBox className="mr-2 h-4 w-4" />
                            Change site
                          </DropdownMenuItem>
                        )}
                      {/* Owner-only: reconnect re-runs OAuth as the clicker
                          and would rebind the connector to their account. */}
                      {isOwner && (
                        <DropdownMenuItem onClick={handleReconnect} disabled={busy !== null}>
                          <Plug className="mr-2 h-4 w-4" />
                          Reconnect
                        </DropdownMenuItem>
                      )}
                      {/* Destroy actions: owner or System Manager. */}
                      {canDestroy && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setConfirmDisconnect(true)}
                            disabled={busy !== null}
                          >
                            <AlertTriangle className="mr-2 h-4 w-4" />
                            Disconnect
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setConfirmDelete(true)}
                            disabled={busy !== null}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete source
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>

          {/* The mismatch warning compares the OAuth identity to the OWNER's
              login — meaningless (and alarming) for another admin merely
              viewing the org connector, so it stays owner-only. */}
          {isOwner && accountMismatch && (
            <p
              className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300"
              title={`Connected as ${source.connected_account_email} - different from your login (${source.owner}).`}
            >
              <CircleAlert className="h-3 w-3" />
              Different account signed in
            </p>
          )}

          {/* Target directory + token security */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Target Directory
              </p>
              <div
                className="mt-1.5 flex items-start gap-1.5 text-sm"
                title={
                  source.provider === 'SharePoint'
                    ? source.sharepoint_site_url || undefined
                    : // Drive: the label collapses to "N folders" past one, so
                      // spell the full list out on hover.
                      source.drive_folder_ids || undefined
                }
              >
                <FolderOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="min-w-0 break-words">{targetDirectoryLabel}</span>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Token Security
              </p>
              <p className="mt-1.5 flex items-center gap-1.5 text-sm">
                {connectionHealthy ? (
                  <>
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                    <span className="text-emerald-400">Active</span>
                  </>
                ) : (
                  <>
                    <CircleAlert className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-300">{connectionStatus}</span>
                  </>
                )}
              </p>
            </div>
          </div>

            {/* Reactive admin-consent (Option B): the tenant requires an admin
                to approve Orgain before this user can connect. Not a failure -
                tell them to get approval, then Reconnect. */}
            {connectionStatus === 'Awaiting Admin Approval' && (
              <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-xs text-amber-200">
                <CircleAlert className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="break-words">
                  Your organisation requires an administrator to approve Orgain
                  for {source.provider}. Once your admin approves, use{' '}
                  <span className="font-medium">Reconnect</span> to finish.
                </span>
              </div>
            )}

            {/* Connected to Microsoft but no site chosen (site picker
                abandoned). Can't sync without a site - prompt to finish. */}
            {needsSite && (
              <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-xs text-amber-200">
                <CircleAlert className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="break-words">
                  Almost done - pick the SharePoint site this source should sync
                  from to finish setup.
                </span>
              </div>
            )}

            {/* ONE connection-error banner. Replaces the old separate
                connection_error line + hardcoded "Expired" banner + runtime
                last_error line so the card never shows two competing errors,
                and the text comes from the server's classified message (e.g. a
                403 reads as a permission problem, not a false "token expired").
                The matching Reconnect button lives in the action row below. */}
            {connectionErrored && (
              <div className="mt-2 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
                <CircleAlert className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span className="break-words">
                  {connectionMessage}
                  {!isOwner && (
                    // Reconnect re-runs OAuth as the clicker, so it's
                    // owner-only — tell the viewing admin who to chase
                    // instead of showing a banner about a button they
                    // don't have.
                    <>
                      {' '}
                      Only the connection owner (
                      {source.owner_full_name || source.owner}) can
                      reconnect.
                    </>
                  )}
                </span>
              </div>
            )}

            {/* Progress: while Syncing show a bar (concrete % of files done);
                while Discovering total is still unknown, show a placeholder;
                Idle/Complete/Error fall back to the per-counter row. */}
            {connectionHealthy && currentStatus === 'Discovering' && (
              <p className="mt-2 text-xs text-muted-foreground">
                Looking for files…
              </p>
            )}

            {/* Pausing: pause accepted, in-flight documents finishing. Say so
                explicitly - the Processing badge alone reads as "pause didn't
                work" while the tail drains. */}
            {connectionHealthy && isPausing && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <PauseCircle className="h-3.5 w-3.5 flex-shrink-0" />
                Pausing - {processingCount > 0
                  ? `${processingCount} document${processingCount === 1 ? '' : 's'} already importing will finish`
                  : 'current work is finishing'}. No new files will start.
                Large documents can take a few minutes each.
              </p>
            )}

            {/* Paused with a backlog: say the queue is parked and how to get it
                moving again - the counter row below only shows a bare Pending
                number, which reads as "stuck" without this. */}
            {connectionHealthy && isPaused && !inProgress && pendingCount > 0 && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <PauseCircle className="h-3.5 w-3.5 flex-shrink-0" />
                Sync paused - {pendingCount} file{pendingCount === 1 ? '' : 's'}{' '}
                waiting. Use Resume sync to continue.
              </p>
            )}

            {/* Queued: job enqueued but no worker free yet (e.g. another source
                is mid-sync). Tell the user it's lined up so the card never reads
                as "nothing happened" after a Sync-now click. */}
            {connectionHealthy && isQueued && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                Queued - waiting for another sync to finish. This will start
                automatically.
              </p>
            )}

            {connectionHealthy && inProgress && currentStatus !== 'Discovering' && (totals.total_discovered ?? 0) > 0 && (() => {
              const total = totals.total_discovered ?? 0;
              const pending = displayedTotals.pending_count ?? 0;
              // Files still being chunked/embedded in the document pipeline.
              // They've been downloaded but aren't in the KB yet, so they're
              // NOT "done" - exclude them from the bar so it doesn't read 100%
              // while documents are still processing.
              const processing = totals.processing_count ?? 0;
              // "Processed" = every file in a terminal state - synced, skipped,
              // failed AND stale. total_discovered counts pending + processing
              // too, so subtract both to avoid the bar overshooting reality.
              const done = Math.max(0, total - pending - processing);
              // Queued between batches: the bar holds here while the */5 cron
              // waits to re-claim the source. Tell the user it'll resume rather
              // than letting the static bar read as "stuck".
              const queued = !isActive && pending > 0;
              return (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-muted-foreground">
                    {done} of {total} processed
                  </div>
                  {queued && (
                    <div className="text-xs text-muted-foreground">
                      {pending} waiting -{' '}
                      {source.enabled
                        ? 'resumes within 5 min'
                        : // Paused/pausing: the resume cron skips disabled
                          // sources, so "within 5 min" would be a lie here.
                          'continues when you resume'}
                    </div>
                  )}
                  {processing > 0 && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Processing {processing} document{processing === 1 ? '' : 's'}…
                    </div>
                  )}
                  {(((totals.failed_count ?? 0) > 0 && !retryInFlight) || (totals.skipped_count ?? 0) > 0) && (
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      {(totals.skipped_count ?? 0) > 0 && (
                        // Clickable → opens View activity, which lists the
                        // per-file skip reasons (otherwise the count is a dead
                        // end). Mirrors the idle-state counter row below.
                        <button
                          type="button"
                          onClick={() => openActivity('Skipped')}
                          className="hover:text-foreground hover:underline"
                          title="View activity to see why files were skipped"
                        >
                          Skipped: <strong>{totals.skipped_count}</strong>
                        </button>
                      )}
                      {(totals.failed_count ?? 0) > 0 && !retryInFlight && (
                        <span>Failed: <strong className="text-destructive">{totals.failed_count}</strong></span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Counter row - Idle / Complete / Error states. Hidden while
                active so it doesn't compete with the bar above, and hidden
                when the source is disconnected (those numbers describe a
                past connection the user can't act on without reconnecting). */}
            {!inProgress &&
              connectionStatus === 'Connected' &&
              ((totals.total_discovered ?? 0) > 0 ||
                (totals.synced_count ?? 0) > 0 ||
                (totals.pending_count ?? 0) > 0 ||
                (totals.skipped_count ?? 0) > 0 ||
                (totals.failed_count ?? 0) > 0 ||
                (totals.stale_count ?? 0) > 0) && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    Discovered: <strong className="text-foreground">{totals.total_discovered ?? 0}</strong>
                  </span>
                  <span>
                    Synced: <strong className="text-emerald-400">{safeSyncedCount}</strong>
                  </span>
                  {(displayedTotals.pending_count ?? 0) > 0 && (
                    <span>
                      Pending: <strong className="text-foreground">{displayedTotals.pending_count}</strong>
                    </span>
                  )}
                  {(totals.skipped_count ?? 0) > 0 && (
                    // Clickable → opens View activity, which lists the per-file
                    // skip reasons (otherwise the count is a dead end).
                    <button
                      type="button"
                      onClick={() => openActivity('Skipped')}
                      className="hover:text-foreground hover:underline"
                      title="View activity to see why files were skipped"
                    >
                      Skipped: <strong>{totals.skipped_count}</strong>
                    </button>
                  )}
                  {(totals.failed_count ?? 0) > 0 && !retryInFlight && (
                    <span>
                      Failed: <strong className="text-destructive">{totals.failed_count}</strong>
                    </span>
                  )}
                  {(totals.stale_count ?? 0) > 0 && (
                    <span>
                      Stale: <strong>{totals.stale_count}</strong>
                    </span>
                  )}
                </div>
              )}

            {/* "Last activity" only for non-complete states - when the sync
                completed, the green "Last sync complete" line below carries the
                timestamp instead, so we don't show the same time twice. */}
            {source.last_activity_at &&
              !inProgress &&
              currentStatus !== 'Complete' &&
              connectionStatus === 'Connected' &&
              // Hide while mandatory setup is still pending (just connected /
              // reconnected, picking a site or folders). The stored timestamp
              // is either from a previous account or a pre-config state, so it
              // reads as confusing next to a "finish setup" prompt.
              !needsSite &&
              !needsFolders && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Last activity {formatDateTime(source.last_activity_at)}
                </p>
              )}

            {/* "Last checked - no changes": discovery ran more recently than the
                last file was processed, so the most recent (often manual) sync
                re-scanned the provider and found nothing new. Without this a
                no-op resync looks like it did nothing - the "Last activity" /
                "Last sync complete" timestamp stays frozen because no files
                were ingested. Surfacing last_discovery_at confirms the run
                happened. Hidden while a sync is in flight (the progress UI
                already speaks for it). Also hidden when the source is Complete
                - the green "Last sync complete" line below folds in the same
                "· no changes" note so we don't stack two near-identical lines. */}
            {checkedNoChanges &&
              !inProgress &&
              currentStatus !== 'Complete' &&
              connectionStatus === 'Connected' && (
                <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                  <RefreshCw className="h-3 w-3 flex-shrink-0" />
                  Last checked {formatDateTime(source.last_discovery_at)}
                  <span className="text-muted-foreground/70">· no changes</span>
                </p>
              )}

            {/* Polling paused itself after a long stretch of no movement -
                surface a refresh affordance so the user isn't stuck wondering. */}
            {pollStalled && pendingCount > 0 && (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Live updates paused - no changes for a while.</span>
                <button
                  type="button"
                  onClick={() => {
                    setPollStalled(false);
                    fetchProgress();
                    onRefresh();
                  }}
                  className="text-primary hover:underline"
                >
                  Refresh
                </button>
              </div>
            )}

{source.next_scheduled_sync_at &&
              connectionStatus === 'Connected' && (
                <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Scheduled:{' '}
                  <span className="font-medium text-foreground">
                    {formatDateTime(source.next_scheduled_sync_at)}
                  </span>
                </p>
              )}

            {/* Runtime (sync-time) error - only when the connection itself is
                healthy. A broken connection routes its message through the
                single banner above, so this avoids a duplicate second error. */}
            {connectionHealthy && progress?.last_error && currentStatus === 'Error' && (
              <p className="mt-2 text-xs text-destructive break-words">
                {progress.last_error}
              </p>
            )}
            {connectionStatus === 'Connected' &&
              currentStatus === 'Complete' &&
              !inProgress && (() => {
                // Prefer the real processing time; fall back to the discovery
                // time so this line is never timestamp-less (e.g. a source that
                // only ever re-checked with no new files has no last_activity_at
                // but does have last_discovery_at). When the latest run was a
                // no-op re-check, append "· no changes" here rather than as a
                // separate stacked line above.
                const ts = source.last_activity_at || source.last_discovery_at;
                return (
                  <p className="mt-2 flex items-center gap-1 text-xs text-emerald-400">
                    <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                    Last sync complete
                    {ts && (
                      <span className="text-muted-foreground">
                        · {formatDateTime(ts)}
                      </span>
                    )}
                    {checkedNoChanges && (
                      <span className="text-muted-foreground/70">· no changes</span>
                    )}
                  </p>
                );
              })()}
        </div>
      </CardContent>

      {showConfig && (
        <CloudSourceConfigModal
          source={source}
          open={showConfig}
          onOpenChange={(o) => {
            setShowConfig(o);
            if (!o) setConfigMandatory(false);
          }}
          onSaved={onRefresh}
          mandatory={configMandatory}
        />
      )}
      {showActivity && (
        <CloudSourceActivityModal
          source={source}
          open={showActivity}
          onOpenChange={setShowActivity}
          onRefresh={onRefresh}
          initialStatusFilter={activityFilter}
        />
      )}

      {showChangeSite && source.provider === 'SharePoint' && (
        <SharePointSitePickerDialog
          open={showChangeSite}
          onOpenChange={setShowChangeSite}
          sourceName={source.name}
          currentSiteUrl={source.sharepoint_site_url}
          onSaved={handleSiteSaved}
          // Finishing first-time setup (no site yet) is mandatory - no Cancel /
          // dismiss. Swapping an already-set site stays optional.
          mandatory={!source.sharepoint_site_url}
          title={
            source.sharepoint_site_url
              ? 'Change SharePoint site'
              : 'Pick a SharePoint site'
          }
          description={
            source.sharepoint_site_url
              ? 'Pick a different SharePoint site for this source. Future syncs will pull from the new site; previously-synced documents from the old site stay in Orgain unless you delete them manually.'
              : 'Finish setting up this source by choosing the SharePoint site it should sync from.'
          }
        />
      )}

      <AlertDialog open={confirmStop} onOpenChange={setConfirmStop}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Stop this sync?</AlertDialogTitle>
            <AlertDialogDescription>
              Files already being imported will finish; everything else goes
              back to the queue. Nothing is deleted, and the sync will not
              restart on its own - use Sync now when you're ready to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'stop'}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleStopSync} disabled={busy === 'stop'}>
              {busy === 'stop' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Stop sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect this source?</AlertDialogTitle>
            <AlertDialogDescription>
              The configuration is kept, but {source.provider} will be signed out.
              You'll need to reconnect before the next sync.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'disconnect'}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnect} disabled={busy === 'disconnect'}>
              {busy === 'disconnect' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmMismatchSync} onOpenChange={setConfirmMismatchSync}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {accountMismatch
                ? 'Sync from a different account?'
                : pendingSyncMode === 'later'
                  ? `Schedule sync for ${syncTimeLabel}?`
                  : 'Confirm sync'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {accountMismatch ? (
                    <>
                      The connected {source.provider} account does not match your
                      Orgain login.{' '}
                      {isDriveProvider
                        ? "Files will be ingested from the connected account's Google Drive, not yours"
                        : "Files will be ingested from this site using the connected account's access, not your login"}
                      {pendingSyncMode === 'later'
                        ? `, on the next scheduled run at ${syncTimeLabel}.`
                        : '.'}
                    </>
                  ) : (
                    <>
                      Review what will be ingested from {source.provider}
                      {pendingSyncMode === 'later'
                        ? ` on the next scheduled run at ${syncTimeLabel}.`
                        : ' now.'}
                    </>
                  )}
                </p>
                {accountMismatch && (
                  // The mismatch is the reason this dialog exists and is the
                  // higher-severity, security-relevant signal (you may ingest
                  // another person's files). Style it as a destructive (red)
                  // warning so it clearly outranks the amber scope box below,
                  // rather than blending into a single wall of amber.
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                    <p className="flex items-center gap-1.5 font-medium">
                      <CircleAlert className="h-3.5 w-3.5 flex-shrink-0" />
                      Different account
                    </p>
                    <p className="mt-1.5">
                      <span className="opacity-80">Connected as: </span>
                      <span className="font-medium">
                        {source.connected_account_email}
                      </span>
                    </p>
                    <p className="mt-1">
                      {/* For a non-owner admin "your login" would be wrong -
                          the mismatch is between the OAuth identity and the
                          connection OWNER's login. */}
                      <span className="opacity-80">
                        {isOwner ? 'Your Orgain login: ' : 'Connection owner: '}
                      </span>
                      <span className="font-medium">{source.owner}</span>
                    </p>
                  </div>
                )}
                {(() => {
                  // Scope summary: what'll actually be pulled from the other
                  // account. The user said "make sure the user is doing this
                  // intentionally" when scope is unrestricted (whole drive,
                  // all file types) - call that out with a stronger amber box.
                  // When scope is set, show exactly what's configured so the
                  // user can confirm.
                  const folderRaw =
                    source.provider === 'Google Drive'
                      ? source.drive_folder_ids
                      : source.sharepoint_folder_paths;
                  const folders = (folderRaw || '').trim();
                  const exts = (source.file_extension_filter || '').trim();
                  const unrestricted = !folders && !exts;
                  return (
                    <div
                      className={
                        unrestricted
                          ? 'rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200'
                          : 'rounded-md border bg-muted/40 p-3 text-xs'
                      }
                    >
                      <p className="font-medium">
                        {unrestricted
                          ? `This will sync the entire ${providerStoreNoun}`
                          : 'What will be synced'}
                      </p>
                      <p className="mt-1">
                        <span className="text-muted-foreground">Folders: </span>
                        <span className="font-medium text-foreground break-all">
                          {folders || (source.provider === 'Google Drive'
                            ? 'All folders (whole drive)'
                            : 'All folders (whole site)')}
                        </span>
                      </p>
                      <p className="mt-1">
                        <span className="text-muted-foreground">File types: </span>
                        {exts ? (
                          <span className="font-medium text-foreground break-all">
                            {exts}
                          </span>
                        ) : (
                          // No filter → the full 17-type list is noisy; collapse
                          // to a label with the exact list available on hover.
                          <span
                            className="font-medium text-foreground"
                            title={SUPPORTED_EXTENSIONS.map((e) => `.${e}`).join(', ')}
                          >
                            All supported types
                          </span>
                        )}
                      </p>
                      {unrestricted && (
                        <p className="mt-2">
                          No folder path or file-extension filter is set, so every
                          supported file in the {providerStoreNoun} will be
                          ingested. Cancel and use Configure first if that isn't
                          what you want.
                        </p>
                      )}
                    </div>
                  );
                })()}
                {accountMismatch && (
                  <p>
                    Continue only if this is intentional. To use your own
                    account, cancel and reconnect this source from its menu.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'sync'}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmMismatchSync} disabled={busy === 'sync'}>
              {busy === 'sync' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {pendingSyncMode === 'later'
                ? accountMismatch
                  ? `Schedule anyway (${syncTimeLabel})`
                  : `Schedule for ${syncTimeLabel}`
                : accountMismatch
                  ? 'Sync anyway'
                  : 'Sync now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmOverrideScheduled}
        onOpenChange={setConfirmOverrideScheduled}
      >
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Sync is already scheduled</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This source is already queued to sync at{' '}
                  <span className="font-medium text-foreground">
                    {source.next_scheduled_sync_at
                      ? formatDateTime(source.next_scheduled_sync_at)
                      : 'the next scheduled time'}
                  </span>
                  .
                </p>
                <p>
                  Run a manual sync now anyway? The scheduled run will be
                  cancelled so the same source isn't synced twice in a few hours.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'sync'}>Keep schedule</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmOverrideScheduled}
              disabled={busy === 'sync'}
            >
              {busy === 'sync' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sync now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="dark bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this source?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the source's configuration and sync history, and
              can't be undone.
              {safeSyncedCount > 0 && (
                <>
                  {' '}This source still has {safeSyncedCount} document
                  {safeSyncedCount === 1 ? '' : 's'} in the knowledge base -
                  remove {safeSyncedCount === 1 ? 'it' : 'them'} from the library
                  first, as a source can't be deleted while documents are linked
                  to it.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'delete'}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={busy === 'delete'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy === 'delete' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default CloudSourceCard;
